import { useEffect, useState } from 'react'
import { Box, Card, IconButton, Toolbar, Grid, Divider, Typography, Paper, Chip, Avatar, Button, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material'
import { ArrowBackIos, FiberManualRecord, Person, AccessTime, DoneAll, ExpandMore, Inbox, MarkChatRead, Archive, Delete, Restore, Visibility } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import './Wall.css'
import { newsRoutes } from '../../routes'
import { useAuth, useUser } from '../../context'
import { useNotificationsContext } from '../../context/Notifications.context'
import { useUsersContext } from '../../context/Users.context'
import NewsReadersList from './NewsReadersList.component'
import { dateWithTime } from '../../config'
import 'react-quill/dist/quill.snow.css'

const MyWallPage = () => {
    const navigate = useNavigate()
    const { userData } = useAuth()
    const { admin, isSuperAdmin } = useUser()
    const { usersMap } = useUsersContext()
    const { noticias, listaLectura, marcarNoticiaComoLeidaEnContexto, marcarTodasComoLeidasEnContexto, getNoticias } = useNotificationsContext()
    const [noticiaSeleccionada, setNoticiaSeleccionada] = useState()
    const [showReaders, setShowReaders] = useState(false)

    const getValidReadersCount = (readBy = []) => {
        const uniqueIds = Array.from(new Set((readBy || []).map(userId =>
            typeof userId === 'string' ? userId : userId?.$oid || userId?._id
        )))
        return uniqueIds.filter(id => !!usersMap[id]).length
    }

    const newsActive = noticias.filter(n => n.state !== false && !n.isRead)
    const newsRead = noticias.filter(n => n.state !== false && n.isRead)
    const newsArchived = noticias.filter(n => n.state === false)

    const seleccionarNoticia = async (noticia) => {
        setNoticiaSeleccionada(noticia)
        setShowReaders(false)
        if (!noticia.isRead && userData?._id && noticia.state !== false) {
            try {
                await newsRoutes.markAsRead(noticia._id, userData._id)
                marcarNoticiaComoLeidaEnContexto(noticia._id)
            } catch (error) {
                console.error('Error al marcar como leída:', error)
            }
        }
    }

    const marcarTodasComoLeidas = async () => {
        if (!userData?._id) return
        try {
            await newsRoutes.markAllAsRead(userData._id)
            marcarTodasComoLeidasEnContexto()
        } catch (error) {
            console.error('Error al marcar todas como leídas:', error)
        }
    }

    const archivarNoticia = async (e, newsId) => {
        e.stopPropagation()
        if (!window.confirm('¿Estás seguro de que deseas archivar esta noticia?')) return
        try {
            await newsRoutes.deleteNews(newsId)
            getNoticias()
            if (noticiaSeleccionada?._id === newsId) setNoticiaSeleccionada(undefined)
        } catch (error) {
            console.error('Error al archivar:', error)
        }
    }

    const restaurarNoticia = async (e, newsId) => {
        e.stopPropagation()
        if (!window.confirm('¿Deseas volver a mostrar esta noticia?')) return
        try {
            await newsRoutes.restoreNews(newsId)
            getNoticias()
        } catch (error) {
            console.error('Error al restaurar:', error)
        }
    }

    const renderNewsItem = (noticia) => (
        <Paper
            key={noticia._id}
            elevation={0}
            onClick={() => seleccionarNoticia(noticia)}
            sx={{
                p: 2,
                mb: 1,
                cursor: 'pointer',
                borderRadius: 2,
                transition: 'all 0.2s',
                position: 'relative',
                border: noticiaSeleccionada?._id === noticia._id ? '1px solid brown' : '1px solid #eee',
                bgcolor: noticiaSeleccionada?._id === noticia._id
                    ? (noticia.isVersionNote ? '#e3f2fd' : '#fff9f9')
                    : (noticia.isVersionNote ? '#f0f7ff' : 'transparent'),
                opacity: noticia.state === false ? 0.7 : 1,
                '&:hover': { bgcolor: noticia.isVersionNote ? '#e1f5fe' : '#f9f9f9', borderColor: 'brown' }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="caption" sx={{
                        color: noticia.state === false ? '#888' : (noticia.isVersionNote ? '#0288d1' : '#666'),
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: 9,
                        mb: 0.5,
                        display: 'block'
                    }}>
                        {noticia.state === false ? 'Archivado' : (noticia.isVersionNote ? 'Nota de Versión' : 'Noticia')}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: noticia.isRead ? 400 : 700, pr: 2, fontSize: '0.85rem' }}>
                        {noticia.title || noticia.titulo}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                        visibility: (admin || isSuperAdmin) ? 'visible' : 'hidden',
                        width: 32,
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <Tooltip title={`Visto por ${getValidReadersCount(noticia.readBy)}`}>
                            <IconButton size="small" sx={{ color: '#bbb', p: 0.5 }}>
                                <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {!noticia.isRead && noticia.state !== false && <FiberManualRecord sx={{ color: 'brown', fontSize: 10 }} />}
                    {(admin || isSuperAdmin) && (
                        noticia.state === false ? (
                            <Tooltip title="Restaurar">
                                <IconButton size="small" onClick={(e) => restaurarNoticia(e, noticia._id)} sx={{ color: '#0288d1', p: 0.5, '&:hover': { bgcolor: '#e3f2fd' } }}>
                                    <Restore sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Archivar">
                                <IconButton size="small" onClick={(e) => archivarNoticia(e, noticia._id)} sx={{ color: '#ccc', p: 0.5, '&:hover': { color: 'red' } }}>
                                    <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        )
                    )}
                </Box>
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mt: 1, fontSize: 10 }}>
                <AccessTime sx={{ fontSize: 10, mr: 0.5 }} />
                {dateWithTime(noticia.createdAt)}
            </Typography>
        </Paper>
    )

    return (
        <Box sx={{ height: '100vh', bgcolor: '#f4f4f4', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ pt: 10, flexGrow: 1, overflow: 'hidden' }}>
                <Card elevation={0} sx={{ height: '100%', borderRadius: 0, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
                        <Toolbar variant="dense" sx={{ p: 0, justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                                    <ArrowBackIos style={{ fontSize: 18 }}/>
                                </IconButton>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'brown' }}>Mi Mural Informativo</Typography>
                            </Box>
                            {newsActive.length > 0 && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DoneAll />}
                                    onClick={marcarTodasComoLeidas}
                                    sx={{ color: 'brown', borderColor: 'brown', fontWeight: 700, borderRadius: 2 }}
                                >
                                    Marcar todas como leídas
                                </Button>
                            )}
                        </Toolbar>
                    </Box>

                    <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        {/* Sidebar: News List Accordions */}
                        <Grid item xs={12} md={3} sx={{ borderRight: '1px solid #eee', bgcolor: '#f8f9fa', height: '100%', overflowY: 'auto' }}>
                            <Box sx={{ p: 0 }}>
                                {listaLectura ? (
                                    <>
                                        <Accordion defaultExpanded elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Inbox sx={{ color: 'brown', fontSize: 20 }} />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Pendientes ({newsActive.length})</Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 1, pt: 0 }}>
                                                {newsActive.map(renderNewsItem)}
                                                {newsActive.length === 0 && (
                                                    <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', py: 2, color: '#999' }}>
                                                        No hay noticias pendientes
                                                    </Typography>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <MarkChatRead sx={{ color: '#666', fontSize: 20 }} />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Historial ({newsRead.length})</Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 1, pt: 0 }}>
                                                {newsRead.map(renderNewsItem)}
                                            </AccordionDetails>
                                        </Accordion>

                                        {(admin || isSuperAdmin) && newsArchived.length > 0 && (
                                            <Accordion elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                                <AccordionSummary expandIcon={<ExpandMore />}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Archive sx={{ color: '#888', fontSize: 20 }} />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Archivo ({newsArchived.length})</Typography>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 1, pt: 0 }}>
                                                    {newsArchived.map(renderNewsItem)}
                                                </AccordionDetails>
                                            </Accordion>
                                        )}
                                    </>
                                ) : (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography>Cargando noticias...</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={(noticiaSeleccionada && (admin || isSuperAdmin)) ? 6 : 9} sx={{ height: '100%', overflowY: 'auto', bgcolor: noticiaSeleccionada?.isVersionNote ? '#f0f4f8' : '#fcfcfc' }}>
                            {noticiaSeleccionada ? (
                                <Box sx={{
                                    p: { xs: 2, md: 6 },
                                    bgcolor: noticiaSeleccionada.isVersionNote ? '#fff' : 'transparent',
                                    borderRadius: noticiaSeleccionada.isVersionNote ? 4 : 0,
                                    boxShadow: noticiaSeleccionada.isVersionNote ? '0 4px 20px rgba(0,0,0,0.05)' : 'none'
                                }}>
                                    <Chip
                                        label={noticiaSeleccionada.isVersionNote ? 'Nota de Versión' : 'Noticia'}
                                        size="small"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 700,
                                            bgcolor: noticiaSeleccionada.isVersionNote ? '#0288d1' : '#eee',
                                            color: noticiaSeleccionada.isVersionNote ? '#fff' : '#666'
                                        }}
                                    />
                                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: '#1a1a1a', lineHeight: 1.2 }}>
                                        {noticiaSeleccionada.title || noticiaSeleccionada.titulo}
                                    </Typography>
                                    {noticiaSeleccionada.subtitle && (
                                        <Typography variant="h6" sx={{ color: '#666', mb: 1, fontWeight: 400 }}>
                                            {noticiaSeleccionada.subtitle}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 3, opacity: 0.8 }}>
                                        <AccessTime sx={{ fontSize: 14, mr: 0.5 }} />
                                        Publicado el {dateWithTime(noticiaSeleccionada.createdAt)}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: 'brown' }}>
                                                <Person />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {(() => {
                                                        const creator = noticiaSeleccionada.createdBy
                                                        if (!creator) return 'Sistema ICV'
                                                        const roleDbName = typeof creator.role === 'object' ? creator.role?.dbName : creator.role
                                                        if (roleDbName === 'superAdmin') return 'Sistema'
                                                        return creator.name ? `${creator.name} ${creator.lastName}`.trim() : 'Sistema ICV'
                                                    })()}
                                                </Typography>
                                                {noticiaSeleccionada.createdBy?.role && (() => {
                                                    const roleDbName = typeof noticiaSeleccionada.createdBy.role === 'object'
                                                        ? noticiaSeleccionada.createdBy.role?.dbName
                                                        : noticiaSeleccionada.createdBy.role
                                                    return roleDbName !== 'superAdmin'
                                                })() && (
                                                    <Typography variant="caption" sx={{ color: 'brown', fontWeight: 600, display: 'block', mt: -0.5 }}>
                                                        {noticiaSeleccionada.createdBy.role.name}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        <Box sx={{
                                            visibility: (admin || isSuperAdmin) ? 'visible' : 'hidden',
                                            minWidth: 140,
                                            display: 'flex',
                                            justifyContent: 'flex-end'
                                        }}>
                                            <Button
                                                size="small"
                                                variant={showReaders ? 'contained' : 'outlined'}
                                                startIcon={<Visibility />}
                                                onClick={() => setShowReaders(!showReaders)}
                                                sx={{
                                                    borderRadius: 2,
                                                    bgcolor: showReaders ? 'brown' : 'transparent',
                                                    color: showReaders ? '#fff' : 'brown',
                                                    borderColor: 'brown',
                                                    '&:hover': { bgcolor: showReaders ? '#5d0e0e' : '#fff9f9', borderColor: 'brown' }
                                                }}
                                            >
                                                {showReaders ? 'Ocultar Lecturas' : `Vistos (${getValidReadersCount(noticiaSeleccionada.readBy)})`}
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ mb: 4 }} />

                                    <Box sx={{ flexGrow: 1 }}>
                                        {(noticiaSeleccionada.urlFile || noticiaSeleccionada.urlVideo || noticiaSeleccionada.urlFoto) && (
                                            <Box sx={{ mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                                                {noticiaSeleccionada.urlVideo ? (
                                                    <video controls src={noticiaSeleccionada.urlVideo} width="100%" style={{ display: 'block' }} />
                                                ) : (
                                                    <img src={noticiaSeleccionada.urlFile || noticiaSeleccionada.urlFoto} alt="news" style={{ width: '100%', display: 'block' }} />
                                                )}
                                            </Box>
                                        )}

                                        <div
                                            className="ql-editor ql-content"
                                            dangerouslySetInnerHTML={{ __html: noticiaSeleccionada.content || noticiaSeleccionada.comentario }}
                                            style={{
                                                fontSize: '1.1rem',
                                                lineHeight: 1.8,
                                                color: '#333',
                                                fontFamily: 'Georgia, serif',
                                                padding: 0
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: 0.5 }}>
                                    <NewspaperIcon sx={{ fontSize: 80, mb: 2, color: '#ccc' }} />
                                    <Typography variant="h6">Selecciona una noticia para leerla</Typography>
                                </Box>
                            )}
                        </Grid>

                        {(noticiaSeleccionada && (admin || isSuperAdmin)) && (
                            <Grid item xs={12} md={3} sx={{ height: '100%', bgcolor: '#fff', borderLeft: '1px solid #eee' }}>
                                <Box sx={{ visibility: showReaders ? 'visible' : 'hidden', height: '100%' }}>
                                    <NewsReadersList readBy={noticiaSeleccionada.readBy} />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Card>
            </Box>
        </Box>
    )
}

const NewspaperIcon = (props) => (
    <svg {...props} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
        <path d="M18 14h-8" /><path d="M15 18h-5" /><path d="M10 6h8v4h-8z" />
    </svg>
)

export default MyWallPage
